import * as wanakana from 'wanakana';
import { createConvertError } from './error';
import { debug } from './logger';
import { getTokenizer } from './tokenizer';
import { withErrorHandling } from './error';
import { CharacterMapping, SegmentMapping } from './types';

/**
 * 将片假名转换为平假名
 */
export const katakanaToHiragana = (text: string): string => {
  if (!text || text.trim() === '') {
    return '';
  }

  // 使用wanakana将片假名转换为平假名
  return wanakana.toHiragana(text);
};

/**
 * 将文本转换为平假名
 */
export const convertToHiragana = async (text: string): Promise<{hiragana: string, mapping: any}> => {
  if (!text || text.trim() === '') {
    return { hiragana: '', mapping: null };
  }

  try {
    const analyzer = await getTokenizer();
    const tokens = analyzer.tokenize(text);

    // 提取读音并确保是平假名
    let hiragana = '';
    for (const token of tokens) {
      // kuromoji返回的reading是片假名，需要转换为平假名
      const reading = token.reading ? katakanaToHiragana(token.reading) : token.surface_form;
      hiragana += reading;
    }

    return {
      hiragana,
      mapping: null
    };
  } catch (error) {
    debug('假名转换失败', error);
    // 回退到简单转换
    return {
      hiragana: wanakana.toHiragana(text),
      mapping: null
    };
  }
};

/**
 * 直接转换为罗马音
 */
export const toRomaji = withErrorHandling(
  async (text: string): Promise<string> => {
    if (!text || text.trim() === '') {
      return '';
    }

    // 检查是否包含汉字
    if (hasKanji(text)) {
      // 如果包含汉字，先尝试获取假名
      try {
        const hiraganaResult = await convertToHiragana(text);
        if (hiraganaResult && hiraganaResult.hiragana) {
          // 将假名转换为罗马音
          return wanakana.toRomaji(hiraganaResult.hiragana);
        }
      } catch (error) {
        debug('无法将汉字转换为假名', error);
        // 如果转换失败，继续使用原始文本
      }
    }

    // 使用 wanakana 进行转换
    return wanakana.toRomaji(text);
  },
  '', // 默认值
  '罗马音转换'
);

/**
 * 转换为平假名
 */
export const toHiragana = withErrorHandling(
  async (text: string): Promise<string> => {
    if (!text || text.trim() === '') {
      return '';
    }

    return wanakana.toHiragana(text);
  },
  '', // 默认值
  '平假名转换'
);

/**
 * 获取罗马音
 */
export const getRomaji = async (text: string): Promise<string> => {
  return toRomaji(text);
};

/**
 * 检查文本是否只包含平假名
 */
export const isHiragana = (text: string): boolean => {
  if (!text || text.trim() === '') {
    return false;
  }

  return wanakana.isHiragana(text);
};

/**
 * 检查文本是否包含汉字
 */
export const hasKanji = (text: string): boolean => {
  if (!text || text.trim() === '') {
    return false;
  }

  return text.split('').some(char => wanakana.isKanji(char));
};

/**
 * 检查文本是否是标点符号
 */
export const isPunctuation = (text: string): boolean => {
  if (!text || text.trim() === '') {
    return false;
  }

  return /^[。、！？…―「」『』（）［］【】〈〉《》〔〕・]+$/.test(text);
};

/**
 * 批量获取单词的罗马音
 */
export const getWordRomaji = async (words: string[]): Promise<string[]> => {
  if (!words || words.length === 0) {
    return [];
  }

  // 使用Promise.all并行处理所有单词
  const results = await Promise.all(
    words.map(word => toRomaji(word))
  );

  return results;
};

/**
 * 处理单个分词段
 */
export const processSegment = async (segment: string): Promise<SegmentMapping> => {
  if (!segment || segment.trim() === '') {
    return {
      segment: '',
      reading: '',
      romaji: '',
      characters: []
    };
  }

  try {
    // 获取读音
    const { hiragana } = await convertToHiragana(segment);
    const romaji = await getRomaji(hiragana || segment);

    // 处理字符级映射
    const characters: CharacterMapping[] = segment.split('').map(char => ({
      character: char,
      romaji: wanakana.isKanji(char) ? '?' : wanakana.toRomaji(char),
      isKanji: wanakana.isKanji(char)
    }));

    return {
      segment,
      reading: hiragana || segment,
      romaji,
      characters
    };
  } catch (error) {
    debug(`处理分词失败: "${segment}"`, error);

    // 回退处理
    return {
      segment,
      reading: segment,
      romaji: await getRomaji(segment),
      characters: segment.split('').map(char => ({
        character: char,
        romaji: wanakana.isKanji(char) ? '?' : wanakana.toRomaji(char),
        isKanji: wanakana.isKanji(char)
      }))
    };
  }
};

/**
 * 将分词段映射扁平化为字符映射列表
 */
export const flattenSegmentMappings = (
  segmentMappings: SegmentMapping[],
  originalText: string
): CharacterMapping[] => {
  if (!segmentMappings || segmentMappings.length === 0) {
    return [];
  }

  // 直接使用每个分词的字符映射
  const result: CharacterMapping[] = [];
  for (const mapping of segmentMappings) {
    result.push(...mapping.characters);
  }

  // 确保结果与原始文本长度一致
  if (result.length !== originalText.length) {
    debug(`字符映射长度 (${result.length}) 与原文本长度 (${originalText.length}) 不一致`);

    // 简单修复：如果长度不匹配，回退到字符级处理
    return originalText.split('').map(char => ({
      character: char,
      romaji: wanakana.isKanji(char) ? '?' : wanakana.toRomaji(char),
      isKanji: wanakana.isKanji(char)
    }));
  }

  return result;
};